import { isUnavailableToken as coreIsUnavailableToken } from '@/utils/responseGuard'

export const HALL_LAUNCH_STORAGE = 'hallLaunchPayload'

export const LOTTERY_IDS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32
]

export function toArray(value) {
  return Array.isArray(value) ? value : []
}

export function matchesCurrency(value, currency) {
  if (!value || !currency) return true
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .includes(currency)
}

export function normalizeHomeGameContent(content) {
  const base = content && typeof content === 'object' ? content : {}
  const sourceList = toArray(
    base.dataList ||
      base.list ||
      base.gameList ||
      base.allGameList ||
      base.records
  )
  const looksLikeFlatGames = sourceList.some((item) => item && item.gid != null)
  const groups = looksLikeFlatGames
    ? [
        {
          name: 'Games',
          sortType: 0,
          types: [
            {
              name: 'Games',
              code: 'games',
              games: sourceList
            }
          ]
        }
      ]
    : sourceList
  const dataList = groups.map((group) => ({
    ...group,
    types: toArray(group && group.types).map((type) => ({
      ...type,
      games: toArray(type && type.games)
    }))
  }))
  return {
    ...base,
    dataList
  }
}

export function getVisibleGameGroups(content, currency, options = {}) {
  const includeVendors = options.includeVendors !== false
  return normalizeHomeGameContent(content).dataList
    .filter((group) => group && group.sortType !== 2)
    .filter((group) => includeVendors || group.sortType !== 1)
    .filter((group) => matchesCurrency(group.currency, currency))
    .map((group) => ({
      ...group,
      types: toArray(group.types)
        .filter((type) => matchesCurrency(type && type.currency, currency))
        .map((type) => ({
          ...type,
          games: toArray(type && type.games)
        }))
    }))
}

export function collectGamesFromGroups(groups, options = {}) {
  const games = []
  const seen = new Set()
  toArray(groups).forEach((group) => {
    toArray(group && group.types).forEach((type) => {
      toArray(type && type.games).forEach((game) => {
        if (!game) return
        const key = game.gid != null ? String(game.gid) : game.gameName
        if (options.dedupe && key && seen.has(key)) return
        if (key) seen.add(key)
        games.push(game)
      })
    })
  })
  return games
}

export function normalizeDemoValue(value) {
  if (value === true || value === 1) return 1
  const text = String(value == null ? '' : value).toLowerCase()
  return ['1', 'true', 'yes', 'demo', 'trial'].includes(text) ? 1 : 0
}

// LAUNCH_UNAVAILABLE_TOKENS 已删除 —— token 分类统一走 responseGuard 核

export function launchMarkerToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

export function isLaunchUnavailableToken(value) {
  return coreIsUnavailableToken(value)
}

function isActiveLaunchFlag(value) {
  const token = launchMarkerToken(value)
  return value === true ||
    value === 1 ||
    ['true', '1', 'yes', 'y', 'on'].includes(token) ||
    isLaunchUnavailableToken(value)
}

function isLaunchOkToken(value) {
  const token = launchMarkerToken(value)
  return token === 'ok' || token === 'success' || token === '0' || token === '200'
}

const LAUNCH_ENVELOPE_KEYS = ['content', 'payload', 'body', 'extra', 'page', 'data']
const LAUNCH_URL_KEYS = [
  'forwardUrl',
  'forward_url',
  'url',
  'gameUrl',
  'game_url',
  'launchUrl',
  'launch_url',
  'playUrl',
  'play_url',
  'redirectUrl',
  'redirect_url',
  'iframeUrl',
  'iframe_url'
]
const LAUNCH_PROOF_KEYS = [
  'launchProof',
  'launch_proof',
  'proof',
  'proofId',
  'proof_id',
  'launchId',
  'launch_id',
  'sessionId',
  'session_id',
  'gameSession',
  'game_session',
  'launchSession',
  'launch_session',
  'sessionToken',
  'session_token',
  'launchToken',
  'launch_token',
  'gameToken',
  'game_token',
  'playToken',
  'play_token',
  'accessToken',
  'access_token',
  'token'
]
const LAUNCH_SOURCE_PROOF_KEYS = [
  'providerSource',
  'launchSource',
  'launch_source',
  'sourceTable',
  'source_table'
]
const FALSEY_LAUNCH_PROOF_TOKENS = ['false', '0', 'no', 'off', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'stub', 'fallback', 'placeholder']
const OPTIMISTIC_LAUNCH_PROOF_TOKENS = ['ok', 'success', 'successful', 'succeed', 'succeeded', 'done', 'complete', 'completed']

function launchEnvelopeObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function unwrapLaunchEnvelopeBody(value, depth = 0) {
  const body = launchEnvelopeObject(value)
  if (!Object.keys(body).length || depth >= 3) return body
  for (const key of LAUNCH_ENVELOPE_KEYS) {
    const nested = body[key]
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) continue
    const unwrapped = unwrapLaunchEnvelopeBody(nested, depth + 1)
    if (!Object.keys(unwrapped).length) continue
    const parent = { ...body }
    LAUNCH_ENVELOPE_KEYS.forEach((envelopeKey) => {
      const envelopeValue = parent[envelopeKey]
      if (envelopeValue && typeof envelopeValue === 'object' && !Array.isArray(envelopeValue)) {
        delete parent[envelopeKey]
      }
    })
    return {
      ...parent,
      ...unwrapped
    }
  }
  return body
}

function collectLaunchEnvelopeBodies(source, depth = 0, out = []) {
  if (!source || typeof source !== 'object' || Array.isArray(source) || depth >= 3) return out
  for (const key of LAUNCH_ENVELOPE_KEYS) {
    const value = source[key]
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue
    const body = unwrapLaunchEnvelopeBody(value)
    if (Object.keys(body).length) out.push(body)
    collectLaunchEnvelopeBodies(value, depth + 1, out)
  }
  return out
}

export function launchResponseContent(payload) {
  if (!payload || typeof payload !== 'object') return {}
  for (const key of LAUNCH_ENVELOPE_KEYS) {
    const value = payload[key]
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const body = unwrapLaunchEnvelopeBody(value)
      if (Object.keys(body).length) return body
    }
  }
  return {}
}

function launchResponseBodies(payload) {
  return collectLaunchEnvelopeBodies(payload)
}

function launchUrlValue(value) {
  return String(value == null ? '' : value).trim()
}

function isSafeHttpLaunchUrl(value) {
  const url = launchUrlValue(value)
  if (!url) return false
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch (_) {
    return false
  }
}

function launchUrlCandidates(payload) {
  const candidates = []
  const addSource = (source) => {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return
    LAUNCH_URL_KEYS.forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(source, key)) return
      candidates.push({
        key,
        value: source[key]
      })
    })
  }
  addSource(payload)
  addSource(launchResponseContent(payload))
  launchResponseBodies(payload).forEach(addSource)
  return candidates
}

function launchProofSources(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return []
  return [
    payload,
    launchResponseContent(payload),
    ...launchResponseBodies(payload)
  ].filter((source) => source && typeof source === 'object' && !Array.isArray(source))
}

function hasFalseLaunchProofMarker(payload) {
  return launchProofSources(payload).some((source) => {
    return [...LAUNCH_PROOF_KEYS, ...LAUNCH_SOURCE_PROOF_KEYS, 'falseProof', 'false_proof'].some((key) => {
      if (!Object.prototype.hasOwnProperty.call(source, key)) return false
      if (key === 'falseProof' || key === 'false_proof') return isActiveLaunchFlag(source[key])
      return isFalseLaunchProofValue(source[key])
    })
  })
}

function isFalseLaunchProofValue(value) {
  const token = launchMarkerToken(value)
  return value === false ||
    value === 0 ||
    FALSEY_LAUNCH_PROOF_TOKENS.includes(token) ||
    isLaunchUnavailableToken(value)
}

function hasMeaningfulLaunchProofValue(key, value) {
  if (isFalseLaunchProofValue(value)) return false
  if (value === true || value === 1) return true
  const token = launchMarkerToken(value)
  if (!token) return false
  if (OPTIMISTIC_LAUNCH_PROOF_TOKENS.includes(token)) return false
  if (LAUNCH_SOURCE_PROOF_KEYS.includes(key)) {
    return token === 'database' ||
      token === 'db' ||
      token.includes('third_game') ||
      token.includes('game_info') ||
      token.includes('real')
  }
  return true
}

function hasBackendLaunchProofValue(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false
  return launchProofSources(payload).some((source) => {
    return [...LAUNCH_PROOF_KEYS, ...LAUNCH_SOURCE_PROOF_KEYS].some((key) => {
      if (!Object.prototype.hasOwnProperty.call(source, key)) return false
      return hasMeaningfulLaunchProofValue(key, source[key])
    })
  })
}

export function hasBackendLaunchProof(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false
  if (hasLaunchUnavailableMarker(payload)) return false
  return hasBackendLaunchProofValue(payload)
}

function requiresBackendLaunchProof(payload) {
  return launchUrlCandidates(payload).length > 0
}

function hasUnsafeLaunchUrlField(payload) {
  return launchUrlCandidates(payload).some(({ value }) => !isSafeHttpLaunchUrl(value))
}

export function launchUnavailableReason(payload, options = {}) {
  if (!payload || typeof payload !== 'object') return ''
  const content = launchResponseContent(payload)
  const bodies = launchResponseBodies(payload)
  if (hasUnsafeLaunchUrlField(payload)) return 'unsafeUrl'
  if (hasFalseLaunchProofMarker(payload)) return 'missingProof'
  if ([payload.success, content.success, ...bodies.map((body) => body.success)].some((value) => value === false || launchMarkerToken(value) === 'false')) {
    return 'unavailable'
  }
  const directFlags = [
    [payload.needLogin, 'needLogin'],
    [payload.need_login, 'needLogin'],
    [payload.readOnly, 'readOnly'],
    [payload.readonly, 'readOnly'],
    [payload.read_only, 'readOnly'],
    [payload.readOnlyQuery, 'readOnly'],
    [payload.read_only_query, 'readOnly'],
    [payload.readOnlyMode, 'readOnly'],
    [payload.read_only_mode, 'readOnly'],
    [payload.readOnlyAction, 'writeBlocked'],
    [payload.read_only_action, 'writeBlocked'],
    [payload.mock, 'unimplemented'],
    [payload.mockMode, 'unimplemented'],
    [payload.mock_mode, 'unimplemented'],
    [payload.fallback, 'unimplemented'],
    [payload.fallbackUsed, 'unimplemented'],
    [payload.fallback_used, 'unimplemented'],
    [payload.mockFallback, 'unimplemented'],
    [payload.mock_fallback, 'unimplemented'],
    [payload.unimplemented, 'unimplemented'],
    [payload.notImplemented, 'unimplemented'],
    [payload.not_implemented, 'unimplemented'],
    [payload.notSupported, 'unimplemented'],
    [payload.not_supported, 'unimplemented'],
    [payload.unsupported, 'unimplemented'],
    [payload.queryOnly, 'readOnly'],
    [payload.query_only, 'readOnly'],
    [payload.onlyQuery, 'readOnly'],
    [payload.only_query, 'readOnly'],
    [payload.blocked, 'blocked'],
    [payload.block, 'blocked'],
    [payload.backendBlocked, 'blocked'],
    [payload.backend_blocked, 'blocked'],
    [payload.backendFailure, 'technicalError'],
    [payload.backend_failure, 'technicalError'],
    [payload.technicalMarker, 'technicalError'],
    [payload.technical_marker, 'technicalError'],
    [payload.technicalError, 'technicalError'],
    [payload.technical_error, 'technicalError'],
    [payload.falseProof, 'missingProof'],
    [payload.false_proof, 'missingProof'],
    [payload.actionBlocked, 'blocked'],
    [payload.action_blocked, 'blocked'],
    [payload.stub, 'unimplemented'],
    [payload.dataUnavailable, 'unimplemented'],
    [payload.data_unavailable, 'unimplemented'],
    [payload.readUnavailable, 'readOnly'],
    [payload.read_unavailable, 'readOnly'],
    [payload.routeUnavailable, 'unimplemented'],
    [payload.route_unavailable, 'unimplemented'],
    [payload.writeBlocked, 'writeBlocked'],
    [payload.write_blocked, 'writeBlocked'],
    [payload.success, 'unavailable'],
    [content.needLogin, 'needLogin'],
    [content.need_login, 'needLogin'],
    [content.readOnly, 'readOnly'],
    [content.readonly, 'readOnly'],
    [content.read_only, 'readOnly'],
    [content.readOnlyQuery, 'readOnly'],
    [content.read_only_query, 'readOnly'],
    [content.readOnlyMode, 'readOnly'],
    [content.read_only_mode, 'readOnly'],
    [content.readOnlyAction, 'writeBlocked'],
    [content.read_only_action, 'writeBlocked'],
    [content.mock, 'unimplemented'],
    [content.mockMode, 'unimplemented'],
    [content.mock_mode, 'unimplemented'],
    [content.fallback, 'unimplemented'],
    [content.fallbackUsed, 'unimplemented'],
    [content.fallback_used, 'unimplemented'],
    [content.mockFallback, 'unimplemented'],
    [content.mock_fallback, 'unimplemented'],
    [content.unimplemented, 'unimplemented'],
    [content.notImplemented, 'unimplemented'],
    [content.not_implemented, 'unimplemented'],
    [content.notSupported, 'unimplemented'],
    [content.not_supported, 'unimplemented'],
    [content.unsupported, 'unimplemented'],
    [content.queryOnly, 'readOnly'],
    [content.query_only, 'readOnly'],
    [content.onlyQuery, 'readOnly'],
    [content.only_query, 'readOnly'],
    [content.blocked, 'blocked'],
    [content.block, 'blocked'],
    [content.backendBlocked, 'blocked'],
    [content.backend_blocked, 'blocked'],
    [content.backendFailure, 'technicalError'],
    [content.backend_failure, 'technicalError'],
    [content.technicalMarker, 'technicalError'],
    [content.technical_marker, 'technicalError'],
    [content.technicalError, 'technicalError'],
    [content.technical_error, 'technicalError'],
    [content.falseProof, 'missingProof'],
    [content.false_proof, 'missingProof'],
    [content.actionBlocked, 'blocked'],
    [content.action_blocked, 'blocked'],
    [content.stub, 'unimplemented'],
    [content.dataUnavailable, 'unimplemented'],
    [content.data_unavailable, 'unimplemented'],
    [content.readUnavailable, 'readOnly'],
    [content.read_unavailable, 'readOnly'],
    [content.routeUnavailable, 'unimplemented'],
    [content.route_unavailable, 'unimplemented'],
    [content.writeBlocked, 'writeBlocked'],
    [content.write_blocked, 'writeBlocked'],
    [content.success, 'unavailable']
  ]
  bodies.forEach((body) => {
    directFlags.push(
      [body.readOnly, 'readOnly'],
      [body.readonly, 'readOnly'],
      [body.read_only, 'readOnly'],
      [body.readOnlyQuery, 'readOnly'],
      [body.read_only_query, 'readOnly'],
      [body.readOnlyMode, 'readOnly'],
      [body.read_only_mode, 'readOnly'],
      [body.readOnlyAction, 'writeBlocked'],
      [body.read_only_action, 'writeBlocked'],
      [body.mock, 'unimplemented'],
      [body.mockMode, 'unimplemented'],
      [body.mock_mode, 'unimplemented'],
      [body.fallback, 'unimplemented'],
      [body.fallbackUsed, 'unimplemented'],
      [body.fallback_used, 'unimplemented'],
      [body.mockFallback, 'unimplemented'],
      [body.mock_fallback, 'unimplemented'],
      [body.unimplemented, 'unimplemented'],
      [body.notImplemented, 'unimplemented'],
      [body.not_implemented, 'unimplemented'],
      [body.notSupported, 'unimplemented'],
      [body.not_supported, 'unimplemented'],
      [body.unsupported, 'unimplemented'],
      [body.queryOnly, 'readOnly'],
      [body.query_only, 'readOnly'],
      [body.onlyQuery, 'readOnly'],
      [body.only_query, 'readOnly'],
      [body.blocked, 'blocked'],
      [body.block, 'blocked'],
      [body.backendBlocked, 'blocked'],
      [body.backend_blocked, 'blocked'],
      [body.backendFailure, 'technicalError'],
      [body.backend_failure, 'technicalError'],
      [body.technicalMarker, 'technicalError'],
      [body.technical_marker, 'technicalError'],
      [body.technicalError, 'technicalError'],
      [body.technical_error, 'technicalError'],
      [body.falseProof, 'missingProof'],
      [body.false_proof, 'missingProof'],
      [body.actionBlocked, 'blocked'],
      [body.action_blocked, 'blocked'],
      [body.stub, 'unimplemented'],
      [body.dataUnavailable, 'unimplemented'],
      [body.data_unavailable, 'unimplemented'],
      [body.readUnavailable, 'readOnly'],
      [body.read_unavailable, 'readOnly'],
      [body.routeUnavailable, 'unimplemented'],
      [body.route_unavailable, 'unimplemented'],
      [body.writeBlocked, 'writeBlocked'],
      [body.write_blocked, 'writeBlocked'],
      [body.needLogin, 'needLogin'],
      [body.need_login, 'needLogin'],
      [body.success, 'unavailable']
    )
  })
  for (const [value, reason] of directFlags) {
    if (reason === 'unavailable') {
      if (value === false || launchMarkerToken(value) === 'false' || isLaunchUnavailableToken(value)) return reason
    } else if (isActiveLaunchFlag(value)) {
      return reason
    }
  }

  const markers = [
    payload.status,
    payload.state,
    payload.result,
    payload.errorCode,
    payload.msg,
    payload.message,
    payload.errorMsg,
    payload.errorMessage,
    payload.reason,
    payload.desc,
    payload.description,
    payload.launchStatus,
    payload.forwardStatus,
    content.status,
    content.state,
    content.result,
    content.code,
    content.errorCode,
    content.msg,
    content.message,
    content.errorMsg,
    content.errorMessage,
    content.reason,
    content.desc,
    content.description,
    content.launchStatus,
    content.forwardStatus,
    content.needLogin,
    content.need_login
  ]
  markers.push(payload.needLogin, payload.need_login)
  bodies.forEach((body) => {
    markers.push(
      body.status,
      body.state,
      body.result,
      body.code,
      body.errorCode,
      body.msg,
      body.message,
      body.errorMsg,
      body.errorMessage,
      body.reason,
      body.desc,
      body.description,
      body.launchStatus,
      body.forwardStatus,
      body.needLogin,
      body.need_login
    )
  })
  if (!options.ignoreTopLevelCode) {
    markers.push(payload.code)
  }
  for (const value of markers) {
    if (!isLaunchUnavailableToken(value)) continue
    const token = launchMarkerToken(value)
    if (token.includes('read')) return 'readOnly'
    if (token.includes('write')) return 'writeBlocked'
    if (
      token.includes('unimplemented') ||
      token.includes('not_implemented') ||
      token.includes('not implemented')
    ) return 'unimplemented'
    return 'unavailable'
  }
  if (requiresBackendLaunchProof(payload) && !hasBackendLaunchProofValue(payload)) return 'missingProof'
  return ''
}

export function hasLaunchUnavailableMarker(payload) {
  return !!launchUnavailableReason(payload)
}

export function launchUnavailableMessage(payload, fallback) {
  const reason = launchUnavailableReason(payload)
  if (reason === 'unsafeUrl') return 'This feature is temporarily unavailable. Please try again later.'
  if (reason === 'missingProof') return 'This feature is temporarily unavailable. Please try again later.'
  if (reason === 'needLogin') return 'Please log in to continue'
  const content = launchResponseContent(payload)
  const explicitMessage = payload && (
    payload.msg ||
    payload.message ||
    payload.errorMsg ||
    content.msg ||
    content.message ||
    content.errorMsg
  )
  if (explicitMessage && !isLaunchUnavailableToken(explicitMessage)) return explicitMessage
  if (reason === 'readOnly') return 'This feature is temporarily unavailable. Please try again later.'
  if (reason === 'writeBlocked') return 'This feature is temporarily unavailable. Please try again later.'
  if (reason === 'unimplemented') return 'This feature is temporarily unavailable. Please try again later.'
  return fallback || 'This feature is temporarily unavailable. Please try again later.'
}

export function getLaunchUrl(payload) {
  const candidates = launchUrlCandidates(payload)
  if (!candidates.length || candidates.some(({ value }) => !isSafeHttpLaunchUrl(value))) return ''
  const candidate = candidates.find(({ value }) => isSafeHttpLaunchUrl(value))
  return candidate ? launchUrlValue(candidate.value) : ''
}

export function isGameLaunchReady(payload) {
  const content = launchResponseContent(payload)
  const bodies = launchResponseBodies(payload)
  const okMarkers = [
    payload && payload.success,
    payload && payload.status,
    payload && payload.state,
    payload && payload.result,
    payload && payload.code,
    payload && payload.errorCode,
    content.success,
    content.status,
    content.state,
    content.result,
    content.code,
    content.errorCode
  ]
  bodies.forEach((body) => {
    okMarkers.push(body.success, body.status, body.state, body.result, body.code, body.errorCode)
  })
  return !!(
    payload &&
    okMarkers.some((value) => value === true || isLaunchOkToken(value)) &&
    !hasLaunchUnavailableMarker(payload) &&
    getLaunchUrl(payload) &&
    (!requiresBackendLaunchProof(payload) || hasBackendLaunchProof(payload))
  )
}

export function isLotteryGame(game, typeCode) {
  const rawGid = game && typeof game === 'object' ? game.gid : game
  const gid = Number(rawGid)
  return (
    LOTTERY_IDS.includes(gid) ||
    ['2000701', '2000702'].includes(String(typeCode || ''))
  )
}

export function isUnavailableLaunchEntry(entry) {
  // 读 fail-open:仅显式 success===false 才视为不可用。
  // ★本函数也作用于无 gid 的分组/分类容器(homeSanitize),故绝不按 gid 缺失判,否则整组被过滤光(=0 游戏)。
  // 不再按 readOnly/mock/fallback 等隐藏(真正启动失败由 launchGame 就绪 + api write guard 兜底;
  // buildLaunchPayload 另行校验 gid 缺失)。
  if (!entry || typeof entry !== 'object') return false
  if (entry.success === false || String(entry.success).trim().toLowerCase() === 'false') return true
  return false
}

export function buildLaunchPayload(game, options = {}) {
  const gameInfo = game && typeof game === 'object' ? game : { gid: game }
  if (isUnavailableLaunchEntry(gameInfo)) return null
  const gid = gameInfo.gid
  if (gid == null || gid === '') return null
  if (isLotteryGame(gameInfo, options.typeCode)) {
    return {
      type: 'lottery',
      lotteryType: gameInfo.gameCode,
      lotteryName: gameInfo.gameName,
      typeCate: options.typeCate != null ? options.typeCate : 1
    }
  }
  return {
    type: 'hall',
    gid,
    isDemo: normalizeDemoValue(options.isDemo),
    from: options.from || 'game',
    typeCate: options.typeCate != null ? options.typeCate : 1,
    gameTab: options.gameTab
  }
}

function safeSetStorageItem(storage, key, value) {
  if (!storage || typeof storage.setItem !== 'function') return false
  try {
    storage.setItem(key, value)
    return true
  } catch (_) {
    return false
  }
}

function canWriteStorage(storage) {
  return !!storage && typeof storage.setItem === 'function'
}

function getLocalStorage() {
  try {
    if (typeof window === 'undefined') return null
    const storage = window.localStorage
    return canWriteStorage(storage) ? storage : null
  } catch (_) {
    return null
  }
}

function getSessionStorage() {
  try {
    if (typeof window === 'undefined') return null
    const storage = window.sessionStorage
    return canWriteStorage(storage) ? storage : null
  } catch (_) {
    return null
  }
}

export function storeHallLaunchPayload(payload) {
  if (!payload || payload.type !== 'hall') return
  const storagePayload = {
    ...payload,
    from: payload.from || 'game',
    isDemo: normalizeDemoValue(payload.isDemo)
  }
  const serialized = JSON.stringify(storagePayload)
  const sessionStorage = getSessionStorage()
  const localStorage = getLocalStorage()
  safeSetStorageItem(sessionStorage, HALL_LAUNCH_STORAGE, serialized)
  safeSetStorageItem(localStorage, HALL_LAUNCH_STORAGE, serialized)
  safeSetStorageItem(localStorage, 'hallFrome', storagePayload.from)
  safeSetStorageItem(localStorage, 'hallFrom', storagePayload.from)
  safeSetStorageItem(localStorage, 'hallGid', String(storagePayload.gid))
  safeSetStorageItem(localStorage, 'hallIsDemo', String(storagePayload.isDemo))
  if (storagePayload.typeCate != null) {
    safeSetStorageItem(localStorage, 'hallTypeCate', String(storagePayload.typeCate))
  }
  if (storagePayload.gameTab != null) {
    safeSetStorageItem(localStorage, 'hallGameTab', String(storagePayload.gameTab))
  }
}

export function launchGame(vm, payload) {
  if (!vm) return
  if (!payload || hasLaunchUnavailableMarker(payload)) {
    if (vm.$toast) {
      vm.$toast({
        message: launchUnavailableMessage(payload, (vm.$lang && vm.$lang.common_txt160) || 'This feature is temporarily unavailable. Please try again later.'),
        icon: 'fail'
      })
    }
    return
  }
  if (requiresBackendLaunchProof(payload) && (!isGameLaunchReady(payload) || !hasBackendLaunchProof(payload))) {
    if (vm.$toast) {
      vm.$toast({
        message: launchUnavailableMessage(
          { status: 'ok', content: { launchProof: false } },
          'This feature is temporarily unavailable. Please try again later.'
        ),
        icon: 'fail'
      })
    }
    return
  }
  storeHallLaunchPayload(payload)
  vm.$launchGame(payload)
}
